import { getDataValue, removeDataValue, setDataValue } from '@juvare/json-data-util';
import { Box, Button, Divider, FormControl, FormHelperText, InputLabel, MenuItem, Popover, Select, TextField, } from '@mui/material';
import React from 'react';
import { useCommonDataTableUiTranslation, usePropChange } from '../../hooks';
import { useAdvancedSearchErrors } from '../hooks';
import { removeValuesFromComponentDependencies } from '../utils/deleteComponentDependencies';
import { normalize } from './QuickSearch';
export const AdvancedSearch = ({ field, operator, value, dataPath, filterData, onChangeFilterData, open, onClose, sx, anchorEl, filterDependencies, }) => {
    var _a;
    // give the same properties to inputs' 'name' attribute
    const initValues = React.useCallback(() => ({
        [field.dataPath]: '',
        [operator.dataPath]: '',
        [value.dataPath]: '',
    }), [field.dataPath, operator.dataPath, value.dataPath]);
    const { t } = useCommonDataTableUiTranslation();
    const normalizedValueFromProps = normalizeObjectValues(getDataValue(filterData, dataPath));
    const [advancedValues, setAdvancedValues] = React.useState(normalizedValueFromProps);
    const [values, setValues] = React.useState(initValues);
    const { validate, errors, resetErrors } = useAdvancedSearchErrors();
    usePropChange(filterData, React.useCallback((newFilterData) => {
        const newValue = getDataValue(newFilterData, dataPath);
        setAdvancedValues((prev) => {
            if (JSON.stringify(newValue) !== JSON.stringify(prev)) {
                return newValue;
            }
            else {
                return prev;
            }
        });
    }, [dataPath]));
    const handleClose = React.useCallback(() => {
        onClose();
        setValues(initValues());
        resetErrors();
    }, [onClose, initValues, resetErrors]);
    const handleChange = React.useCallback((event) => {
        setValues((prev) => {
            const inputPath = event.target.name;
            const inputValue = event.target.value;
            const result = Object.assign(Object.assign({}, prev), { [inputPath]: inputValue });
            const inputsToValidate = [inputPath];
            // clear current operator if newly selected field does not contain such operator
            if (inputPath === field.dataPath) {
                const operatorSelected = !!result[operator.dataPath];
                if (operatorSelected) {
                    const selectedFieldOption = field.options.find((fieldOption) => inputValue === fieldOption.value);
                    const fieldContainsOperator = selectedFieldOption === null || selectedFieldOption === void 0 ? void 0 : selectedFieldOption.operatorOptions.some((fieldOperatorOption) => result[operator.dataPath] === fieldOperatorOption.value);
                    if (!fieldContainsOperator) {
                        result[operator.dataPath] = '';
                        inputsToValidate.push(operator.dataPath);
                    }
                }
            }
            validate(result, inputsToValidate);
            return result;
        });
    }, [field.dataPath, field.options, operator.dataPath, validate]);
    const applyValues = React.useCallback((values) => {
        const newNormalizedValue = normalizeObjectValues(values);
        const showError = validate(newNormalizedValue);
        if (showError) {
            return;
        }
        onChangeFilterData((prevFilterData) => {
            if (!prevFilterData[dataPath]) {
                return setComponentValue(prevFilterData, `${dataPath}.#0+`, newNormalizedValue, filterDependencies);
            }
            else {
                return setComponentValue(prevFilterData, `${dataPath}.#${advancedValues.length}+`, newNormalizedValue, filterDependencies);
            }
        });
        handleClose();
        resetErrors();
    }, [resetErrors, advancedValues, validate, handleClose, onChangeFilterData, dataPath, filterDependencies]);
    return (React.createElement(Popover, { open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        }, transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
        } },
        React.createElement(Box, { sx: [
                {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    pb: 0,
                    gap: 2,
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ], "data-test": 'advanced-search-widget' },
            React.createElement(FormControl, { error: errors[field.dataPath] },
                React.createElement(InputLabel, { id: field.dataPath }, field.label),
                React.createElement(Select, { labelId: field.dataPath, label: field.label, value: values[field.dataPath], name: field.dataPath, onChange: handleChange, inputProps: { 'data-test': field.dataTest } }, field.options.map(({ label, value }) => (React.createElement(MenuItem, { key: value, value: value }, label)))),
                errors[field.dataPath] && React.createElement(FormHelperText, { error: true },
                    field.label,
                    " is required.")),
            React.createElement(FormControl, null,
                React.createElement(InputLabel, { id: operator.dataPath }, operator.label),
                React.createElement(Select, { error: errors[operator.dataPath], labelId: operator.dataPath, label: operator.label, value: values[operator.dataPath], name: operator.dataPath, onChange: handleChange, inputProps: { 'data-test': operator.dataTest } }, (_a = field.options
                    .find((fieldOption) => fieldOption.value === values[field.dataPath])) === null || _a === void 0 ? void 0 : _a.operatorOptions.map(({ label, value }) => (React.createElement(MenuItem, { key: value, value: value }, label)))),
                errors[operator.dataPath] && React.createElement(FormHelperText, { error: true },
                    operator.label,
                    " is required.")),
            React.createElement(FormControl, null,
                React.createElement(TextField, { error: errors[value.dataPath], variant: 'outlined', onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            applyValues(values);
                        }
                    }, inputProps: { 'aria-label': value.label, 'data-test': value.dataTest }, value: values[value.dataPath], name: value.dataPath, label: value.label, onChange: handleChange }),
                errors[value.dataPath] && React.createElement(FormHelperText, { error: true },
                    value.label,
                    " is required.")),
            React.createElement(Divider, { variant: 'fullWidth' })),
        React.createElement(Box, { sx: { display: 'flex', padding: 1, gap: 1 } },
            React.createElement(Button, { sx: { flex: 1 }, "aria-label": t('cancel'), onClick: handleClose }, t('cancel')),
            React.createElement(Button, { sx: { flex: 1 }, variant: 'contained', "aria-label": t('apply'), onClick: () => applyValues(values) }, t('apply')))));
};
function setComponentValue(data, dataPath, value, filterDependencies) {
    const newData = JSON.parse(JSON.stringify(data !== null && data !== void 0 ? data : {}));
    if (value !== null) {
        setDataValue(newData, dataPath, value);
    }
    else {
        removeDataValue(newData, dataPath);
        removeValuesFromComponentDependencies(newData, dataPath, filterDependencies);
    }
    return newData;
}
function normalizeObjectValues(value) {
    const result = {};
    for (const i in value) {
        result[i] = normalize(value[i]);
    }
    return result;
}
